<template>
  <form-layout :label="label" :required="required" :optional="optional">
    <div class="position-relative">
      <input type="text"
        class="form-control"
        :class="errorMessage ? 'is-invalid' : ''"
        autocomplete="off"
        :placeholder="placeholder"
        v-model="searchBuildingName"
        @input="onInput"
        @blur="onBlur"
        @focus="onFocus">
      <div v-if="shouldShowDropdown"
        class="position-absolute bg-white border rounded mt-1"
        style="width: 100%; z-index: 1000; max-height: 300px; overflow-y: auto;">
        <div v-for="(buildingSearchResult, index) in filteredResults"
          :key="buildingSearchResult.id"
          class="px-3 py-2"
          :class="selectedIndex === index ? 'bg-light' : ''"
          style="cursor: pointer;"
          @click="selectBuilding(buildingSearchResult)"
          @mouseover="selectedIndex = index">
          {{ buildingSearchResult.name }}
        </div>
      </div>
      <div class="invalid-feedback">{{ errorMessage }}</div>
    </div>
    <span v-if="warningMessage" class="form-text text-secondary">{{ warningMessage }}</span>
  </form-layout>
</template>
<script>
import BaseVuexFormItem from "@/views/molecules/formItems/BaseVuexFormItem";
import FormLayout from "@/views/templates/FormLayout";

export default {
  name: "VuexBuildingTextFormItem",
  extends: BaseVuexFormItem,
  components: {
    FormLayout,
  },
  props: {
    label: {
      type: String,
      default: '依頼物件選択'
    },
    required: {
      type: Boolean,
      default: false
    },
    optional: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      required: true
    },
    minlength: {
      type: [Number, null],
      default: null
    },
    placeholder: {
      type: String,
      required: true
    },
    searchType: {
      type: Number,
      required: true
    },
  },
  emits: ['select'],
  data() {
    return {
      searchBuildingName: null,
      isFocused: false,
      selectedIndex: -1,
    }
  },
  computed : {
    warningMessage() {
      if (!!this.searchBuildingName && this.value === null) {
        return "入力された依頼物件が存在しません。";
      }
      return null;
    },
    filteredResults() {
      const keyword = this.searchBuildingName;
      if (!keyword) return [];

      const minLen = this.minlength || 0;
      const normalizedSearch = this.normalizeSearchText(keyword);

      return this.options.filter((building) => {
        const normalizedName = this.normalizeSearchText(building.name);
        const normalizedNameKana = building.name_kana
          ? this.normalizeSearchText(building.name_kana)
          : "";

        if (keyword.length < minLen) {
          return (
            normalizedName === normalizedSearch ||
            normalizedNameKana === normalizedSearch
          );
        }

        if (this.searchType === 1) {
          return (
            normalizedName.includes(normalizedSearch) ||
            normalizedNameKana.includes(normalizedSearch)
          );
        }

        if (this.searchType === 2) {
          return (
            normalizedName.startsWith(normalizedSearch) ||
            normalizedNameKana.startsWith(normalizedSearch)
          );
        }

        return (
          normalizedName === normalizedSearch ||
          normalizedNameKana === normalizedSearch
        );
      });
    },
    shouldShowDropdown() {
      return this.isFocused && this.filteredResults.length > 0;
    },
  },
  mounted() {
    if (this.value) {
      const filtered = this.options.filter((building) => building.id === this.value);
      this.searchBuildingName = filtered.length > 0 ? filtered[0].name : null;
    }
  },
  methods: {
    normalizeSearchText(str) {
      let result = String(str ?? '').normalize('NFKC');
      result = result
        .replace(/[‐‑‒–—―−ー]/g, '-')
        .replace(/[〜～∼]/g, '~')
        .replace(/[／∕⁄]/g, '/')
        .replace(/[＼￥¥]/g, '\\')
        .replace(/[・･·•‧∙]/g, '・')
        .replace(/[‘’]/g, "'")
        .replace(/[“”]/g, '"');
      result = result.replace(/[\u30A1-\u30F6]/g, function(c) {
        return String.fromCharCode(c.charCodeAt(0) - 0x60);
      });
      return result;
    },
    onInput() {
      this.selectedIndex = -1;
      const normalizedSearch = this.normalizeSearchText(this.searchBuildingName ?? '');
      const exactMatchedBuilding = this.options.find((building) => {
        const normalizedName = this.normalizeSearchText(building.name ?? '');
        const normalizedNameKana = building.name_kana
          ? this.normalizeSearchText(building.name_kana)
          : '';
        return (
          normalizedName === normalizedSearch ||
          normalizedNameKana === normalizedSearch
        );
      });
      if (exactMatchedBuilding) {
        this.value = exactMatchedBuilding.id;
        this.$emit('select', this.value);
        return;
      }
      this.value = null;
      this.$emit('select', this.value);
    },
    onBlur() {
      setTimeout(() => {
        this.isFocused = false;
      }, 200);
    },
    onFocus() {
      this.isFocused = true;
    },
    selectBuilding(building) {
      this.searchBuildingName = building.name;
      this.value = building.id;
      this.isFocused = false;
      this.$emit('select', this.value);
    },
  },
};
</script>
